<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { z } from 'zod'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

const AdminTemplate = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  os_family: z.string(),
  os_name: z.string().optional().default(''),
  os_version: z.string().optional().default(''),
  proxmox_template_vmid: z.number().nullable().optional(),
  proxmox_node: z.string().nullable().optional(),
  min_disk_gb: z.number().optional().default(10),
  active: z.boolean().default(true),
  sort_order: z.number().optional().default(0)
})
type AdminTemplate = z.infer<typeof AdminTemplate>

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const templates = ref<AdminTemplate[]>([])
const submitting = ref(false)
const editing = ref(false)

const form = ref({
  slug: '',
  name: '',
  os_family: 'linux' as 'linux' | 'bsd',
  os_name: '',
  os_version: '',
  proxmox_template_vmid: null as number | null,
  proxmox_node: '',
  min_disk_gb: 10 as number | null,
  sort_order: 0 as number | null,
  active: true
})

const osFamilyItems = [
  { value: 'linux', title: 'Linux' },
  { value: 'bsd', title: 'BSD' }
]

const canSubmit = computed(
  () =>
    !submitting.value &&
    form.value.slug.trim() !== '' &&
    form.value.name.trim() !== '' &&
    form.value.proxmox_template_vmid !== null &&
    form.value.proxmox_node.trim() !== ''
)

function resetForm() {
  editing.value = false
  form.value = {
    slug: '',
    name: '',
    os_family: 'linux',
    os_name: '',
    os_version: '',
    proxmox_template_vmid: null,
    proxmox_node: '',
    min_disk_gb: 10,
    sort_order: 0,
    active: true
  }
}

function editTemplate(tpl: AdminTemplate) {
  editing.value = true
  form.value = {
    slug: tpl.slug,
    name: tpl.name,
    os_family: tpl.os_family === 'bsd' ? 'bsd' : 'linux',
    os_name: tpl.os_name,
    os_version: tpl.os_version,
    proxmox_template_vmid: tpl.proxmox_template_vmid ?? null,
    proxmox_node: tpl.proxmox_node ?? '',
    min_disk_gb: tpl.min_disk_gb,
    sort_order: tpl.sort_order,
    active: tpl.active
  }
}

async function fetchTemplates() {
  loading.value = true
  try {
    const res = await useAxios<null, unknown>('GET', '/admin/vserver/templates', true)
    if (res?.status === STATUS_CODES.OK) {
      const parsed = z.array(AdminTemplate).safeParse(res.data)
      templates.value = parsed.success ? parsed.data : []
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerTemplates' })
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!canSubmit.value) return
  submitting.value = true
  try {
    await useAxios('POST', '/admin/vserver/templates', true, {
      slug: form.value.slug.trim(),
      name: form.value.name.trim(),
      os_family: form.value.os_family,
      os_name: form.value.os_name.trim(),
      os_version: form.value.os_version.trim(),
      proxmox_template_vmid: form.value.proxmox_template_vmid,
      proxmox_node: form.value.proxmox_node.trim(),
      min_disk_gb: form.value.min_disk_gb ?? 10,
      sort_order: form.value.sort_order ?? 0,
      active: form.value.active
    })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.vserver.admin.templates.title'),
      message: editing.value
        ? t('dashboard.vserver.admin.templates.updated')
        : t('dashboard.vserver.admin.templates.created')
    })
    resetForm()
    await fetchTemplates()
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerTemplates:submit' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getErrorMessage(err)
    })
  } finally {
    submitting.value = false
  }
}

onMounted(fetchTemplates)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.templates.title')"
    :subtitle="t('dashboard.vserver.admin.templates.subtitle')"
    icon="mdi-disc"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">
          {{ editing ? $t('dashboard.vserver.admin.templates.editTitle') : $t('dashboard.vserver.admin.templates.addTitle') }}
        </h3>
        <button v-if="editing" type="button" class="btn-secondary" @click="resetForm">
          {{ $t('dashboard.vserver.admin.templates.cancelEdit') }}
        </button>
      </div>
      <p class="hint">{{ $t('dashboard.vserver.admin.templates.prereqHint') }}</p>
      <div class="form-grid">
        <v-text-field
          v-model="form.slug"
          :label="$t('dashboard.vserver.admin.templates.slug')"
          :disabled="editing"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input"
        />
        <v-text-field v-model="form.name" :label="$t('dashboard.vserver.admin.templates.name')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-select v-model="form.os_family" :items="osFamilyItems" :label="$t('dashboard.vserver.admin.templates.osFamily')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model="form.os_name" :label="$t('dashboard.vserver.admin.templates.osName')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="form.os_version" :label="$t('dashboard.vserver.admin.templates.osVersion')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model.number="form.proxmox_template_vmid" type="number" :label="$t('dashboard.vserver.admin.templates.vmid')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model="form.proxmox_node" :label="$t('dashboard.vserver.admin.templates.node')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model.number="form.min_disk_gb" type="number" :label="$t('dashboard.vserver.admin.templates.minDisk')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model.number="form.sort_order" type="number" :label="$t('dashboard.vserver.admin.templates.sortOrder')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-switch v-model="form.active" :label="$t('dashboard.vserver.admin.templates.active')" color="#00B0FF" density="compact" hide-details class="form-input narrow" />
      </div>
      <div class="actions">
        <button type="button" class="btn-create" :disabled="!canSubmit" @click="submit">
          <v-icon icon="mdi-content-save" size="18" />
          {{ editing ? $t('dashboard.vserver.admin.templates.save') : $t('dashboard.vserver.admin.templates.add') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else-if="templates.length === 0" class="state-box">
      <v-icon icon="mdi-disc" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.vserver.admin.templates.empty') }}</p>
    </div>

    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header tpl-grid">
          <span>{{ $t('dashboard.vserver.admin.templates.table.name') }}</span>
          <span>{{ $t('dashboard.vserver.admin.templates.table.slug') }}</span>
          <span>{{ $t('dashboard.vserver.admin.templates.table.os') }}</span>
          <span>{{ $t('dashboard.vserver.admin.templates.table.vmid') }}</span>
          <span>{{ $t('dashboard.vserver.admin.templates.table.node') }}</span>
          <span>{{ $t('dashboard.vserver.admin.templates.table.active') }}</span>
          <span></span>
        </div>
        <div v-for="tpl in templates" :key="tpl.id" class="table-row tpl-grid">
          <span>{{ tpl.name }}</span>
          <span class="mono">{{ tpl.slug }}</span>
          <span>{{ tpl.os_name }} {{ tpl.os_version }}</span>
          <span class="mono">{{ tpl.proxmox_template_vmid ?? '-' }}</span>
          <span class="mono">{{ tpl.proxmox_node ?? '-' }}</span>
          <span>
            <span class="status-chip" :class="tpl.active ? 'status-success' : 'status-grey'">
              {{ tpl.active ? $t('dashboard.vserver.admin.templates.activeYes') : $t('dashboard.vserver.admin.templates.activeNo') }}
            </span>
          </span>
          <span>
            <button type="button" class="btn-secondary" @click="editTemplate(tpl)">
              {{ $t('dashboard.vserver.admin.templates.edit') }}
            </button>
          </span>
        </div>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.hint {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0 0 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  align-items: center;
}

@media (max-width: 900px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.form-input.narrow {
  max-width: 160px;
}

.actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #00b0ff, #1fe2d4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-secondary {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.8rem;
  cursor: pointer;
}

.data-table {
  overflow-x: auto;
}

.tpl-grid {
  grid-template-columns: 1.2fr 1fr 1fr 0.6fr 0.9fr 0.6fr 0.6fr;
  min-width: 880px;
}

.table-header, .table-row {
  display: grid;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  color: rgba(200, 218, 255, 0.6);
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}
</style>
